/**
 * Turns the payload of a failed request into a short, log-safe summary.
 *
 * A record such as "Reg No not found in system" says nothing about which number was typed, so the
 * input has to reach the log for the error to be actionable. It cannot go there verbatim: the same
 * payloads carry identity documents, contact details and free-text comments. Every field is
 * therefore classified explicitly, and a field nobody has classified yet is reported by name only.
 */

/** Values that may be logged as they are: business identifiers, none of which names a person. */
const PLAIN_FIELDS = [
  'bookingId',
  'bookingRef',
  'brand',
  'currentMileage',
  'dealerName',
  'endDate',
  'identityType',
  'mileage',
  'modelCode',
  'modelYear',
  'operationCode',
  'partNumber',
  'preferredContactMethodType',
  'quantity',
  'referenceNumber',
  'registrationNumber',
  'requestId',
  'serviceType',
  'startDate',
  'telephoneType',
  'timeUnits',
  'vin',
  'vinNumber',
];

/** Personal data: the log keeps only enough to tell two attempts apart. */
const MASKED_FIELDS = [
  'address',
  'city',
  'customerId',
  'email',
  'firstName',
  'lastName',
  'mobile',
  'name',
  'passportNumber',
  'phoneNumber',
  'postalCode',
  'suburb',
  'surname',
];

/** Reported in a column of its own, so the summary does not repeat it. */
const LIFTED_FIELDS = ['dealerCode'];

/** Free text the user writes: only its length is logged, since anything can end up in there. */
const TEXT_FIELDS = ['additionalNotes', 'requirements', 'serviceComments'];

const MAX_FIELDS = 12;
const MAX_VALUE_LENGTH = 40;
const MAX_CONTEXT_LENGTH = 400;

/**
 * Describes the request body of a failed call, e.g. `registrationNumber=CA123456, customerId=***789`.
 * Returns undefined when there is nothing worth reporting, so callers can leave the message alone.
 */
export function describeRequestContext(payload: unknown): string | undefined {
  const parts = describeRecord(payload, '', true);

  if (!parts.length) {
    return undefined;
  }

  return parts.join(', ').slice(0, MAX_CONTEXT_LENGTH);
}

/**
 * Reads the payload axios kept on the failed request. A body is a JSON string by the time the
 * request has been sent, while query parameters are still an object.
 */
export function readRequestPayload(config: unknown): unknown {
  const source = config as { data?: unknown; params?: unknown } | undefined;

  if (!source) {
    return undefined;
  }

  const body = typeof source.data === 'string' ? parseJson(source.data) : source.data;

  return body === undefined || body === null ? source.params : body;
}

function describeRecord(payload: unknown, prefix: string, allowNesting: boolean): string[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const parts: string[] = [];

  Object.keys(record).forEach((key) => {
    if (parts.length >= MAX_FIELDS) {
      return;
    }

    const value = record[key];
    const name = prefix ? `${prefix}.${key}` : key;

    // A nested object is worth one more level: a booking carries the customer and the vehicle
    // inside it, and that is exactly where the interesting fields live.
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (allowNesting) {
        parts.push(...describeRecord(value, name, false));
      }
      return;
    }

    const described = describeValue(key, value);

    if (described !== undefined) {
      parts.push(`${name}=${described}`);
    }
  });

  return parts;
}

function describeValue(key: string, value: unknown): string | undefined {
  // An empty field is one the user did not fill in. Dropping it keeps the summary short and, on
  // the login form, leaves only the identifier that was actually used.
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (LIFTED_FIELDS.indexOf(key) !== -1) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.length ? `<${value.length} items>` : undefined;
  }

  const text = String(value);

  if (TEXT_FIELDS.indexOf(key) !== -1) {
    return `<text:${text.length}>`;
  }

  if (MASKED_FIELDS.indexOf(key) !== -1) {
    // The domain of an address says which mailbox failed far better than its last characters do,
    // and it identifies nobody on its own.
    const at = key === 'email' ? text.indexOf('@') : -1;

    return at > 0 ? `***${text.slice(at)}` : mask(text);
  }

  if (PLAIN_FIELDS.indexOf(key) !== -1 || typeof value === 'boolean') {
    return text.slice(0, MAX_VALUE_LENGTH);
  }

  // An unclassified field is reported by name only: it may well hold personal data, and the fact
  // that it was filled in is already a hint.
  return '<set>';
}

/** Reads the dealer the failed call was about, which the log keeps in a field of its own. */
export function readDealerCode(payload: unknown): string | undefined {
  const record = payload as Record<string, unknown> | undefined;
  const value = record && typeof record === 'object' ? record.dealerCode : undefined;

  return typeof value === 'string' && value ? value : undefined;
}

function mask(value: string): string {
  const text = value.trim();

  return text.length > 4 ? `***${text.slice(-3)}` : `***(${text.length})`;
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}
